import ComposeBody from './ComposeBody';
import ComposeBottom, { ComposeBottomHandle } from './ComposeBottom';
import ComposeTop, { ComposeTopHandle } from './ComposeTop';
import StickyBottomBorder from './StickyBottomBorder';
import { observer } from 'mobx-react-lite';
import { lazyModule as attachmentsTelemetryModule } from 'owa-mail-attachments-telemetry';
import { lazyUpdateRibbonWidth } from 'owa-editor-ribbonplugin';
import { lazyCloseExpressionPane } from 'owa-expressions-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import { useKeydownHandler } from 'owa-hotkeys';
import { addInitialAction, InitialActions } from 'owa-initial-actions';
import { lazyCreateMailComposeAdapter } from 'owa-mail-addins';
import shouldAddinsPopulate from 'owa-mail-addins/lib/utils/shouldAddinsPopulate';
import trySaveMessage, { isSaving } from 'owa-mail-compose-actions/lib/actions/trySaveMessage';
import { ComposeViewState, ComposeControl, ComposeOperation } from 'owa-mail-compose-store';
import send from '../commands/send';
import { isGroupScenario } from 'owa-mail-compose-store/lib/utils/isGroupComposeViewState';
import { isViewStateDirty } from 'owa-mail-compose-actions/lib/utils/viewStateUtils';
import { FocusComponent, lazyResetFocus, lazyRegisterComponent } from 'owa-mail-focus-manager';
import { getCommands } from 'owa-mail-hotkeys/lib/utils/MailModuleHotKeys';
import { lazySubscribeToResizeEvent, lazyUnsubscribeFromResizeEvent } from 'owa-resize-event';
import { DarkModeContext } from 'owa-theme';
import updateContentToViewState from 'owa-editor/lib/utils/updateContentToViewState';
import workaroundFocusIssueForFirefox from 'owa-editor/lib/utils/workaroundFocusIssueForFirefox';
import { findTabByData, TabState } from 'owa-tab-store';
import { lazyCloseChecker } from 'owa-editor-acc-checker';
import * as React from 'react';
import {
    default as discardCompose,
    shouldShowDiscardConfirmDialog,
} from 'owa-mail-compose-actions/lib/actions/discardCompose';
import getAutoFocusControl from 'owa-mail-compose-actions/lib/utils/getAutoFocusControl';
import {
    lazyInitializeAddinsForItem,
    lazyDeinitializeAddinsForItem,
    lazyCloseTaskPaneAddinCommand,
    lazyOnLaunchEventTriggered,
    getComposeHostItemIndex,
    getSurfaceNewAddinId,
    getExtensionForAddinId,
} from 'owa-addins-core';
import { ExtensibilityModeEnum } from 'owa-addins-types';
import setInitialVisiblePart from 'owa-editor-docking-plugin/lib/actions/setInitialVisiblePart';
import tryScrollIntoView from 'owa-editor-docking-plugin/lib/utils/tryScrollIntoView';
import { useCustomTimeout } from 'owa-react-hooks/lib/useCustomTimeout';
import setIsAutorunComposeEventTriggered from 'owa-addins-editor-plugin/lib/actions/setIsAutorunComposeEventTriggered';
import ProjectionContext from 'owa-popout-v2/lib/context/ProjectionContext';
import LaunchEventType from 'owa-service/lib/contract/LaunchEventType';
import { lazyShowAdminInstalledNewAddinFRECallout } from 'owa-addins-exp-ui';

const styles = require('./Compose.scss');
const classNames = require('classnames');

export interface ComposeProps {
    viewState: ComposeViewState;
    initialActions: InitialActions;
    className?: string;
    senderPersona?: JSX.Element;
    onUnmountFocusCallback?: () => void;
    children?: React.ReactNode;
    isSxS?: boolean;
}

const AUTO_SAVE_INTERVAL = 30000;
const AUTO_SAVE_INTERNAL_GROUPS = 300000; // 5 minutes
const FOCUS_TIMER = 10;

/**
 * Scope for artificial resize events.
 * Empty string is the default scope.
 *
 * When a FlexPane mounts or unmounts, the
 * artificial resize it fires is on this scope.
 **/
const RESIZE_EVENT_SCOPE: string = '';

function useAutoSaveTimer({ viewState }: ComposeProps) {
    const autoSaveDraft = React.useCallback(() => {
        updateContentToViewState(viewState);

        // Don't auto save if item creation is in progress
        if (!isSaving(viewState) && isViewStateDirty(viewState)) {
            trySaveMessage(viewState, true /*isAutoSave*/);
        }
    }, [viewState]);

    React.useEffect(() => {
        const autoSaveDraftTimer = window.setInterval(
            autoSaveDraft,
            isGroupScenario(viewState) ? AUTO_SAVE_INTERNAL_GROUPS : AUTO_SAVE_INTERVAL
        );

        return () => {
            window.clearInterval(autoSaveDraftTimer);

            if (viewState.shouldUpdateContentOnDispose) {
                autoSaveDraft();
            }
        };
    }, [autoSaveDraft, viewState]);
}

function useResizeHandlers(
    { viewState, initialActions }: ComposeProps,
    rootDiv: React.RefObject<HTMLDivElement>,
    bottom: React.RefObject<ComposeBottomHandle>
) {
    const [setResizeTimer] = useCustomTimeout();

    const onResize = React.useCallback(
        () =>
            setResizeTimer(() => {
                if (rootDiv.current) {
                    const clientWidth = rootDiv.current.clientWidth;
                    lazyUpdateRibbonWidth.importAndExecute(viewState.ribbon, clientWidth);
                }
                if (bottom.current) {
                    bottom.current.resize();
                }
            }, 1),
        [viewState.ribbon]
    );

    React.useEffect(() => {
        let resizeEventScope = getResizeEventScope(viewState);
        const subscriptionPromise = lazySubscribeToResizeEvent
            .import()
            .then(subscribeToResizeEvent => {
                subscribeToResizeEvent(
                    onResize,
                    resizeEventScope,
                    rootDiv.current?.ownerDocument?.defaultView
                );
            });

        return () => {
            subscriptionPromise.then(() => {
                lazyUnsubscribeFromResizeEvent.importAndExecute(onResize, resizeEventScope);
            });
        };
    }, [onResize, viewState]);

    React.useEffect(() => {
        addInitialAction(initialActions, onResize);
    }, []);
}

function useFocusHandler(
    { viewState, onUnmountFocusCallback }: ComposeProps,
    top: React.RefObject<ComposeTopHandle>,
    body: React.RefObject<ComposeBody>
) {
    const [setFocusTimer, , isFocusTimerActive] = useCustomTimeout();

    React.useEffect(() => {
        /**
         * Callback when the compose pane gains focus from focus manager
         */
        const focus = () => {
            if (!isFocusTimerActive()) {
                // Always do focus in async mode because this function can be called from an action,
                // and at that time the component may not be updated by React yet.
                // Use setTimeout rather than RAF to allow browser finish a reflow first and user can
                // see the view, the we do focus to avoid a force reflow.
                setFocusTimer(() => {
                    if (body.current && top.current) {
                        const controlToFocus = getAutoFocusControl(viewState);

                        if (controlToFocus == ComposeControl.Editor) {
                            body.current.trySetFocus();
                        } else {
                            top.current.trySetFocus(controlToFocus);
                        }
                    }
                }, FOCUS_TIMER);
            }
            return true;
        };

        const unregisterPromise = lazyRegisterComponent.importAndExecute(
            FocusComponent.MailCompose,
            focus
        );
        focus();

        return () => {
            unregisterPromise.then(cb => cb());

            if (onUnmountFocusCallback) {
                onUnmountFocusCallback();
            } else {
                lazyResetFocus.importAndExecute();
            }
        };
    }, []);
}

function useAddins({ viewState }: ComposeProps) {
    const initialized = React.useRef(false);
    const hostIndex = React.useRef(getComposeHostItemIndex(viewState.composeId));
    const targetWindow = React.useContext(ProjectionContext);

    React.useEffect(() => {
        if (!initialized.current && shouldAddinsPopulate(viewState)) {
            Promise.all([
                lazyCloseTaskPaneAddinCommand.import(),
                lazyCreateMailComposeAdapter.import(),
                lazyInitializeAddinsForItem.import(),
                lazyOnLaunchEventTriggered.import(),
            ]).then(
                ([
                    closeTaskPaneAddinCommand,
                    createMailComposeAdapter,
                    initializeAddinsForItem,
                    onLaunchEventTriggered,
                ]) => {
                    initialized.current = true;

                    closeTaskPaneAddinCommand(hostIndex.current);
                    const adapter = createMailComposeAdapter(viewState);
                    initializeAddinsForItem(
                        hostIndex.current,
                        adapter,
                        true /* renderTaskPaneInPanel */,
                        targetWindow
                    ).then(() => {
                        if (
                            isFeatureEnabled('addin-autoRun') &&
                            isFeatureEnabled('addin-autoRun-newComposeEvent') &&
                            shouldTriggerAutorunComposeEvent(viewState)
                        ) {
                            onLaunchEventTriggered(
                                hostIndex.current,
                                LaunchEventType.OnNewMessageCompose
                            );
                            setIsAutorunComposeEventTriggered(viewState.addin);
                        }
                        if (isFeatureEnabled('addin-exp-pinNewAdminAddinsFRECallout')) {
                            // One of the treatment in experiment to pin newly installed admin addins
                            // and show lightning nudge for latest addin.
                            targetWindow.requestAnimationFrame(() => {
                                showLatestNewAdminAddinFRECallout();
                            });
                        }
                    });
                }
            );

            return () => {
                lazyDeinitializeAddinsForItem
                    .import()
                    .then(deinitializeAddinsForItem =>
                        deinitializeAddinsForItem(hostIndex.current, targetWindow)
                    );
            };
        } else {
            return () => {};
        }
    }, [viewState, viewState.protectionViewState?.IRMData?.RmsTemplateId]);
}

function showLatestNewAdminAddinFRECallout() {
    let newAddinId = getSurfaceNewAddinId(ExtensibilityModeEnum.MessageCompose);
    if (newAddinId) {
        let newAddinName: string = 'New Add-in';
        const extension = getExtensionForAddinId(newAddinId);
        if (extension) {
            newAddinName = extension.DisplayName;
        }

        lazyShowAdminInstalledNewAddinFRECallout.importAndExecute(
            ExtensibilityModeEnum.MessageCompose,
            newAddinId,
            newAddinName
        );
    }
}

function shouldTriggerAutorunComposeEvent(viewState: ComposeViewState) {
    if (
        !viewState.addin.isAutorunComposeEventTriggered &&
        (viewState.operation === ComposeOperation.New ||
            viewState.operation === ComposeOperation.Reply ||
            viewState.operation === ComposeOperation.ReplyAll ||
            viewState.operation === ComposeOperation.Forward)
    ) {
        return true;
    }
    return false;
}

function useForgottenAttachments() {
    React.useEffect(() => {
        // This module is only orchestrators, we load the module here to import them.
        attachmentsTelemetryModule.import();
    }, []);
}

function useCleanupPanesOnUnmount(
    { viewState }: ComposeProps,
    rootDiv: React.RefObject<HTMLDivElement>
) {
    React.useEffect(
        // Function returns function to execute on unmount
        () => () => {
            const targetWindow = rootDiv.current.ownerDocument.defaultView;

            if (viewState.accChecker.isOpen) {
                lazyCloseChecker
                    .import()
                    .then(closeChecker => closeChecker(viewState.accChecker, targetWindow));
            }

            lazyCloseExpressionPane.importAndExecute(viewState.expressionId, targetWindow);
        },
        []
    );
}

export default observer(function Compose(props: ComposeProps) {
    const { className, viewState, children } = props;

    const rootDiv = React.useRef<HTMLDivElement>();
    const top = React.useRef<ComposeTopHandle>();
    const body = React.useRef<ComposeBody>();
    const bottom = React.useRef<ComposeBottomHandle>();

    useAutoSaveTimer(props);
    useResizeHandlers(props, rootDiv, bottom);
    useFocusHandler(props, top, body);
    useAddins(props);
    useForgottenAttachments();
    useCleanupPanesOnUnmount(props, rootDiv);

    const sendMessage = React.useCallback(() => {
        send(viewState, rootDiv.current.ownerDocument.defaultView);
    }, [viewState]);
    const discardDraft = React.useCallback(() => {
        if (shouldShowDiscardConfirmDialog(viewState) && rootDiv.current) {
            workaroundFocusIssueForFirefox(rootDiv.current.ownerDocument);
        }
        updateContentToViewState(viewState);
        discardCompose(viewState, rootDiv.current.ownerDocument.defaultView);
    }, [viewState]);

    useKeydownHandler(rootDiv, getCommands().sendMail, sendMessage, {
        allowHotkeyOnTextFields: true,
    });
    useKeydownHandler(rootDiv, getCommands().discardDraft, discardDraft, {
        allowHotkeyOnTextFields: true,
    });
    useKeydownHandler(
        rootDiv,
        getCommands().saveDraft,
        React.useCallback(() => {
            updateContentToViewState(viewState);
            trySaveMessage(viewState);
        }, [viewState]),
        { allowHotkeyOnTextFields: true }
    );

    const rootDivRefCallback = React.useCallback(
        (div: HTMLDivElement) => {
            rootDiv.current = div;
            if (viewState?.docking) {
                setInitialVisiblePart(viewState.docking, div);

                if (div) {
                    tryScrollIntoView(viewState, viewState.docking);
                }
            }
        },
        [viewState]
    );

    return (
        <DarkModeContext.Provider value={viewState.isDarkMode}>
            <div
                className={classNames(className, styles.composeOuterContainer)}
                ref={rootDivRefCallback}>
                <div className={!viewState.isDarkMode ? styles.composeInnerContainer : undefined}>
                    <ComposeTop {...props} ref={top} />
                    <ComposeBody {...props} ref={body} />
                    <ComposeBottom
                        ref={bottom}
                        isHidden={viewState.docking.isBottomDocking}
                        {...props}
                    />
                    {viewState.docking.useStickyDocking && (
                        <StickyBottomBorder
                            editorViewState={viewState}
                            dockingViewState={viewState.docking}
                        />
                    )}
                </div>
                {children}
            </div>
        </DarkModeContext.Provider>
    );
});

function getResizeEventScope(viewState: ComposeViewState) {
    const tab = findTabByData(viewState.composeId);
    if (tab?.state == TabState.Popout) {
        return tab.id;
    } else {
        return RESIZE_EVENT_SCOPE;
    }
}
